import { format, isTomorrow, parseISO } from "date-fns";
import { ja } from "date-fns/locale";
import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useMutation, useQuery } from "urql";
import {
  AvailableScheduleQuery,
  BookInterviewMutation,
  CancelBookedInterviewMutation,
} from "../../graphql/candidatePublic.graphql";
import { StakeholderInterviewNotice } from "./StakeholderInterviewNotice";

interface TimeSlot {
  startTime: string;
  endTime: string;
}

export function Schedule() {
  const params = useParams();
  const token = params.token as string;

  // URL の :token を Authorization ヘッダに載せる (詳細: graphql/client.ts の authExchange)
  // useMemo しないと毎レンダーで新オブジェクトになり、useQuery が context 差分と判断して
  // 再フェッチ→再レンダーの無限ループになる
  const tokenContext = useMemo(() => ({ candidateToken: token }), [token]);

  const [result, reexecute] = useQuery({
    query: AvailableScheduleQuery,
    context: tokenContext,
  });
  const [, executeBook] = useMutation(BookInterviewMutation);
  const [, executeCancel] = useMutation(CancelBookedInterviewMutation);

  const [booking, setBooking] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null);

  const data = result.data?.availableSchedule;
  const loading = result.fetching;
  const error = result.error?.message ?? null;

  const handleBook = async (slot: TimeSlot) => {
    if (!window.confirm("この時間で予約しますか？")) return;

    setSelectedSlot(slot.startTime);
    setBooking(true);
    try {
      const bookResult = await executeBook(
        { input: { startTime: slot.startTime } },
        tokenContext,
      );
      const payload = bookResult.data?.bookInterview;
      if (bookResult.error || !payload?.success) {
        alert(payload?.errorMessage || "予約に失敗しました");
      } else {
        alert("予約が完了しました。カレンダーをご確認ください。");
        reexecute({ requestPolicy: "network-only" });
      }
    } finally {
      setBooking(false);
      setSelectedSlot(null);
    }
  };

  const handleCancel = async () => {
    if (
      !window.confirm(
        "予約をキャンセルしますか？キャンセル後は、この画面から別の日時を選び直せます。",
      )
    )
      return;

    setCancelling(true);
    try {
      const cancelResult = await executeCancel({}, tokenContext);
      const payload = cancelResult.data?.cancelBookedInterview;
      if (cancelResult.error || !payload?.success) {
        alert(payload?.errorMessage || "キャンセルに失敗しました");
        return;
      }
      alert("予約をキャンセルしました。別の日時を選択できます。");
      reexecute({ requestPolicy: "network-only" });
    } finally {
      setCancelling(false);
    }
  };

  const groupSlotsByDate = (slots: TimeSlot[]) => {
    const grouped: { [key: string]: TimeSlot[] } = {};
    for (const slot of slots) {
      const date = format(parseISO(slot.startTime), "yyyy-MM-dd");
      if (!grouped[date]) {
        grouped[date] = [];
      }
      grouped[date].push(slot);
    }

    return Object.entries(grouped)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([date, dateSlots]) => ({
        date,
        slots: dateSlots.sort(
          (a, b) =>
            parseISO(a.startTime).getTime() - parseISO(b.startTime).getTime(),
        ),
      }));
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[var(--color-bg-subtle-gray)]">
        <div className="space-y-4 text-center">
          <div className="relative">
            <div className="mx-auto h-12 w-12 animate-spin rounded-full border-4 border-[var(--color-border-default)] border-t-[var(--color-primary-500)]" />
          </div>
          <p className="font-medium text-[var(--color-text-secondary)]">
            読み込み中...
          </p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[var(--color-bg-subtle-gray)] px-4">
        <div className="w-full max-w-[500px] overflow-hidden rounded-xl border border-[var(--color-border-default)] bg-white shadow-[0_2px_8px_rgba(0,0,0,0.05)]">
          <div className="border-[var(--color-border-default)] border-b p-6">
            <h2 className="font-bold text-[var(--color-primary-500)] text-lg">
              エラー
            </h2>
          </div>
          <div className="p-6">
            <p className="font-medium text-[#dc2626]">
              {error || "データの取得に失敗しました"}
            </p>
          </div>
        </div>
      </div>
    );
  }

  const isCompleted = data.candidate.status === "COMPLETED";
  const hasCurrentBooking = data.currentBooking?.startTime != null;
  const groupedSlots = groupSlotsByDate(data.availableSlots);

  return (
    <div className="min-h-screen bg-[var(--color-bg-subtle-gray)]">
      <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6 sm:py-12 lg:px-8">
        {/* ヘッダーセクション */}
        <div className="mb-8 border-[var(--color-primary-500)] border-l-4 pl-5">
          <h1 className="mb-2 font-bold text-[32px] text-[var(--color-primary-500)]">
            {data.scheduleConfig.scheduleTitle}
          </h1>
          <p className="mb-3 font-medium text-[var(--color-text-secondary)] text-sm">
            {data.candidate.name} 様
            {hasCurrentBooking || isCompleted
              ? ""
              : "、ご都合の良い日時を選択してください"}
          </p>
          <div className="inline-flex items-center gap-2 rounded-lg border border-[#b3d9f0] bg-[var(--color-primary-50)] px-4 py-2">
            <svg
              className="h-4 w-4 text-[var(--color-primary-500)]"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <span className="font-medium text-[13px] text-[var(--color-primary-500)]">
              所要時間: {data.scheduleConfig.durationMinutes}分
            </span>
          </div>
        </div>

        {data.stakeholderContext && (
          <StakeholderInterviewNotice
            recipientNames={data.stakeholderContext.recipientNames}
          />
        )}

        {/* インタビュー実施後の表示。再予約は担当者(インタビュワー/CS)経由のため予約導線は出さない */}
        {isCompleted && (
          <div className="mb-6 rounded-xl border border-[var(--color-border-default)] bg-white p-6 shadow-[0_2px_8px_rgba(0,0,0,0.05)]">
            <div className="mb-4 flex items-center gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#ecfdf5]">
                <svg
                  className="h-5 w-5 text-[#059669]"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <h2 className="font-bold text-[var(--color-text-primary)] text-lg">
                インタビューは完了しています
              </h2>
            </div>

            <p className="text-[13px] text-[var(--color-text-secondary)]">
              ご協力いただきありがとうございました。ご不明な点がある場合は、担当者までご連絡ください。
            </p>

            <div className="mt-4 border-[var(--color-border-default)] border-t pt-4">
              <button
                type="button"
                onClick={() => window.close()}
                className="cursor-pointer border-none bg-transparent p-0 font-medium text-[13px] text-[var(--color-primary-500)] underline hover:no-underline"
              >
                この画面を閉じる
              </button>
            </div>
          </div>
        )}

        {/* 予約済みの場合の表示。日程変更はキャンセルしてから選び直す導線に寄せる (MNT-9179) */}
        {!isCompleted && hasCurrentBooking && (
          <div className="mb-6 rounded-xl border border-[var(--color-border-default)] bg-white p-6 shadow-[0_2px_8px_rgba(0,0,0,0.05)]">
            <div className="mb-4 flex items-center gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#ecfdf5]">
                <svg
                  className="h-5 w-5 text-[#059669]"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <h2 className="font-bold text-[var(--color-text-primary)] text-lg">
                予約が確定しています
              </h2>
            </div>

            <div className="mb-5 rounded-lg bg-[var(--color-primary-50)] p-4">
              <p className="mb-1 font-semibold text-[var(--color-primary-500)] text-base">
                {data.currentBooking?.startTime &&
                  format(
                    parseISO(data.currentBooking.startTime),
                    "yyyy年MM月dd日（E）HH:mm",
                    { locale: ja },
                  )}
                {data.currentBooking?.endTime &&
                  ` 〜 ${format(parseISO(data.currentBooking.endTime), "HH:mm", { locale: ja })}`}
              </p>
            </div>

            <p className="text-[13px] text-[var(--color-text-secondary)]">
              日程を変更する場合は、予約をキャンセルしてから別の日時を選び直してください。
            </p>

            <div className="mt-4 flex flex-wrap items-center gap-4 border-[var(--color-border-default)] border-t pt-4">
              <button
                type="button"
                onClick={handleCancel}
                disabled={cancelling}
                className={`rounded-lg border border-[var(--color-red-500)] bg-white px-4 py-2 font-medium text-[13px] text-[var(--color-red-500)] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-red-500)] focus-visible:ring-offset-2 ${
                  cancelling
                    ? "cursor-not-allowed opacity-60"
                    : "cursor-pointer hover:bg-[var(--color-red-50)]"
                }`}
              >
                {cancelling ? "キャンセル中..." : "予約をキャンセルする"}
              </button>
              <button
                type="button"
                onClick={() => window.close()}
                className="cursor-pointer border-none bg-transparent p-0 font-medium text-[13px] text-[var(--color-primary-500)] underline hover:no-underline"
              >
                この画面を閉じる
              </button>
            </div>
          </div>
        )}

        {/* メインコンテンツ（予約がない時のみ表示） */}
        {!isCompleted && !hasCurrentBooking && (
          <div className="overflow-hidden rounded-xl border border-[var(--color-border-default)] bg-white shadow-[0_2px_8px_rgba(0,0,0,0.05)]">
            <div className="p-6">
              {data.availableSlots.length === 0 ? (
                <div className="px-6 py-12 text-center">
                  <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full border border-[var(--color-border-default)] bg-[var(--color-bg-subtle-gray)]">
                    <svg
                      className="h-8 w-8 text-[#9ca3af]"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                    </svg>
                  </div>
                  <div className="space-y-2">
                    <p className="font-semibold text-[var(--color-text-secondary)] text-base">
                      利用可能な時間枠がありません
                    </p>
                    <p className="text-[#9ca3af] text-sm">
                      しばらく時間をおいてから再度ご確認ください
                    </p>
                  </div>
                </div>
              ) : (
                <div className="space-y-10">
                  {groupedSlots.map(({ date, slots }) => {
                    const dateObj = parseISO(date);
                    const isTomorrowDate = isTomorrow(dateObj);

                    return (
                      <div key={date} className="space-y-4">
                        {/* 日付ヘッダー */}
                        <div className="mb-5 border-[var(--color-border-default)] border-b-2 pb-3">
                          <div className="flex flex-wrap items-center gap-3">
                            <h3 className="font-bold text-2xl text-[var(--color-primary-500)]">
                              {format(dateObj, "yyyy年MM月dd日", {
                                locale: ja,
                              })}
                            </h3>
                            <span className="font-medium text-[13px] text-[var(--color-text-secondary)]">
                              ({format(dateObj, "E", { locale: ja })})
                            </span>
                            {isTomorrowDate && (
                              <span className="rounded-xl bg-[var(--color-orange-500)] px-3 py-1 font-semibold text-white text-xs">
                                明日
                              </span>
                            )}
                          </div>
                        </div>

                        {/* 時間スロットグリッド */}
                        <div className="flex flex-wrap gap-3">
                          {slots.map((slot, index) => {
                            const startTime = parseISO(slot.startTime);
                            const endTime = parseISO(slot.endTime);
                            const isSelected = selectedSlot === slot.startTime;
                            const isBooking = booking && isSelected;

                            return (
                              <button
                                type="button"
                                key={`${slot.startTime}-${index}`}
                                onClick={() => handleBook(slot)}
                                disabled={booking}
                                className={`relative flex min-w-[110px] flex-col items-center justify-center rounded-lg px-4 py-3 font-medium text-sm transition-all duration-200 ${
                                  isSelected
                                    ? "h-[88px] -translate-y-0.5 border-2 border-[var(--color-primary-500)] bg-[var(--color-primary-500)] text-white shadow-[0_4px_12px_rgba(5,108,171,0.2)]"
                                    : "h-[88px] border border-[var(--color-border-default)] bg-white text-[var(--color-text-primary)] shadow-[0_1px_3px_rgba(0,0,0,0.1)] hover:-translate-y-0.5 hover:border-[var(--color-primary-500)] hover:bg-[var(--color-primary-50)] hover:shadow-[0_4px_12px_rgba(5,108,171,0.15)]"
                                } ${booking ? "cursor-not-allowed" : "cursor-pointer"} ${booking && !isSelected ? "opacity-50" : ""}`}
                              >
                                {isBooking && (
                                  <div className="absolute inset-0 flex items-center justify-center rounded-lg bg-[rgba(5,108,171,0.1)]">
                                    <div className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent" />
                                  </div>
                                )}
                                <span
                                  className={`mb-1 font-bold text-lg ${isSelected ? "text-white" : "text-[var(--color-primary-500)]"}`}
                                >
                                  {format(startTime, "HH:mm", { locale: ja })}
                                </span>
                                <span
                                  className={`font-normal text-[11px] ${isSelected ? "text-white/80" : "text-[var(--color-text-secondary)]"}`}
                                >
                                  ～{format(endTime, "HH:mm", { locale: ja })}
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
